import {
  createContext,
  ReactNode,
  useContext,
  useLayoutEffect,
  useState
} from 'react';

const PortalContainerContext = createContext<HTMLElement | null>(null);

/**
 * Where overlays (dialogs, menus, tooltips, drag previews) portal to: the
 * nearest `PortalContainerScope`'s element, else `#tailwind-portals`.
 */
export function usePortalContainer(): HTMLElement {
  const scoped = useContext(PortalContainerContext);
  return scoped ?? document.getElementById('tailwind-portals') ?? document.body;
}

/**
 * Overlays opened from `children` portal into an element of their own that
 * carries `className`, so they look like the region that opened them (a
 * theme's `dark`).
 */
export function PortalContainerScope({
  className,
  children
}: {
  className?: string;
  children: ReactNode;
}) {
  const parent = usePortalContainer();
  const [container] = useState(() => document.createElement('div'));
  useLayoutEffect(() => {
    parent.appendChild(container);
    return () => container.remove();
  }, [parent, container]);
  useLayoutEffect(() => {
    container.className = className ?? '';
  }, [container, className]);
  return (
    <PortalContainerContext.Provider value={container}>
      {children}
    </PortalContainerContext.Provider>
  );
}
